// v6 F12 — Generic zone-level template picker + toggleable work lines.
// Used by:
//   - Facade zones (exterior_area) → filters template_context ∈ {facade_paint, facade_limewash, wood_exterior}
//   - Stair zones (stair_zone) → filters template_context === "stairs"
//
// Works with normal Measurement objects (unlike JoineryBlock which uses
// zone.joinery_works). Toggling a template line creates/removes a
// Measurement bound to the zone via zone_id + price_code_id.
//
// Sticky selection lives in WorkZone.zone_template_id (backend supports
// this field since v6 F12).
import React, { useMemo, useEffect } from "react";
import { Check } from "lucide-react";
import { UNIT_LABEL } from "../../lib/calculator";
import RawNumInput from "./RawNumInput";
import { EditableDescription } from "./EditableDescription";

const fmtEUR = (n) => {
  try {
    return new Intl.NumberFormat("nl-BE", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(+n || 0).replace(/\u00A0/g, " ");
  } catch { return `€ ${(+n || 0).toFixed(2).replace(".", ",")}`; }
};

const uuid = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `m_${Math.random().toString(36).slice(2)}`;

/**
 * @param {object} props
 * @param {object} props.zone            The WorkZone
 * @param {Array}  props.measurements    Measurements bound to this zone (zone_id === zone.id)
 * @param {Array}  props.templates       All templates from API
 * @param {Array}  props.priceCodes      All price codes from API
 * @param {Array}  props.contexts        Allowed template_context values, e.g. ["facade_paint","facade_limewash","wood_exterior"]
 * @param {string} props.label           Section label (e.g. "Gevel-sjabloon", "Trap-sjabloon")
 * @param {string} props.accentColor     CSS color for accent (e.g. "#6B8E6B" or "#7F4124")
 * @param {number} props.zoneQty         Current zone qty (net area or perimeter) — driver for auto-qty
 * @param {string} props.zoneQtyUnit     "m2" | "lm" | "piece"
 * @param {string} props.mode            "facade" (m² auto zone_net) | "stair" (piece auto=treads, m² opt-in)
 * @param {number} props.stairTreads     For mode="stair" — number of treads (auto-qty for piece)
 * @param {object} props.calc            Calc result (for zone-line totals)
 * @param {Function} props.onUpdateZone  Called with a patch: onUpdateZone({...})
 * @param {Function} props.onAddMeasurement
 * @param {Function} props.onRemoveMeasurement
 * @param {Function} props.onUpdateMeasurement
 */
export default function ZoneTemplateBlock({
  zone, measurements = [], templates = [], priceCodes = [],
  contexts = [], label = "Sjabloon", accentColor = "#B07A4F",
  zoneQty = 0, zoneQtyUnit = "m2", mode = "facade", stairTreads = 0, calc,
  onUpdateZone, onAddMeasurement, onRemoveMeasurement, onUpdateMeasurement,
}) {
  const activeTemplateId = zone.zone_template_id || "";

  const pcById = useMemo(() => {
    const m = {};
    priceCodes.forEach((p) => { m[p.id] = p; });
    return m;
  }, [priceCodes]);

  // Filter templates on template_context (primary) with categories fallback (legacy)
  const relevantTemplates = useMemo(() => {
    const active = (templates || []).filter((t) => t.active !== false);
    return active.filter((t) => {
      if (contexts.length && t.template_context && contexts.includes(t.template_context)) {
        return true;
      }
      return false;
    });
  }, [templates, contexts]);

  const activeTemplate = useMemo(
    () => relevantTemplates.find((t) => t.id === activeTemplateId) || null,
    [relevantTemplates, activeTemplateId]
  );
  // v6 F16.2 — Idem als in JoineryBlock: detecteer verouderd (active=false)
  // sjabloon zodat we een rustige amber waarschuwing tonen i.p.v. rode error.
  const legacyTemplate = useMemo(
    () => (templates || []).find((t) => t.id === activeTemplateId && t.active === false) || null,
    [templates, activeTemplateId]
  );
  const templateExistsInDb = !!activeTemplateId && !!(templates || []).find((t) => t.id === activeTemplateId);
  const templateMissing = !!activeTemplateId && templates.length > 0 && !templateExistsInDb;
  const templateWrongCtx = !!activeTemplateId && templateExistsInDb && !legacyTemplate && !activeTemplate;
  const templateLegacy = !!legacyTemplate;

  // v6 F12b — Stair mode: auto-sync qty=stairTreads for all currently
  // toggled piece-based measurements (in this zone) when treads change.
  useEffect(() => {
    if (mode !== "stair" || !onUpdateMeasurement) return;
    for (const meas of measurements) {
      const pc = pcById[meas.price_code_id];
      if (!pc || pc.unit !== "piece") continue;
      if ((+meas.quantity || 0) !== stairTreads) {
        onUpdateMeasurement(meas.id, { quantity: stairTreads });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, stairTreads]);

  // Map: price_code_id → measurement (for "is this line currently checked?")
  const measByPc = useMemo(() => {
    const m = {};
    for (const meas of measurements) {
      if (meas.price_code_id) m[meas.price_code_id] = meas;
    }
    return m;
  }, [measurements]);

  // Formula + initial qty for a template line, depending on mode + unit.
  // - Facade mode:  m² → zone_net (auto), lm → linear (manual length), piece → piece (manual qty), fixed → fixed(1)
  // - Stair mode:   piece → piece with qty=stairTreads (auto),
  //                 lm    → linear (manual length),
  //                 m²    → custom (manual, opt-in via "Gebruik trapoppervlakte" checkbox),
  //                 fixed → fixed(1), hour → hour (manual qty).
  const buildMeasurementFromLine = (line) => {
    const pc = pcById[line.price_code_id];
    if (!pc) return null;
    const description = line.description_override || pc.description || "";
    const unit_price_override = line.unit_price_override != null ? +line.unit_price_override : null;
    let formula = "fixed";
    let quantity = 0;
    let length = 0;
    if (pc.unit === "m2") {
      if (mode === "stair") {
        // Opt-in: start als 'custom' qty=0 zodat de regel niks toevoegt totdat
        // de gebruiker "Gebruik trapoppervlakte" aanvinkt (of qty manueel invult).
        formula = "custom";
        quantity = 0;
      } else {
        // Facade / default: auto-inherit zone.net_area
        formula = "zone_net";
      }
    } else if (pc.unit === "lm") {
      formula = "linear";
      length = 0; // gebruiker vult zelf in
    } else if (pc.unit === "piece") {
      formula = "piece";
      quantity = mode === "stair" && stairTreads > 0 ? stairTreads : 0;
    } else if (pc.unit === "hour") {
      formula = "hour";
      quantity = 0;
    } else if (pc.unit === "fixed") {
      formula = "fixed";
      quantity = 1;
    }
    return {
      id: uuid(),
      zone_id: zone.id,
      room_id: null,
      description,
      formula,
      length, width: 0, height: 0,
      quantity,
      multiplier: 1, coats: 2,
      price_code_id: line.price_code_id,
      action_code: pc.code,
      unit_price_override,
    };
  };

  const setTemplate = (tplId) => {
    const oldTpl = activeTemplate;
    const newTpl = relevantTemplates.find((t) => t.id === tplId) || null;

    // v6 F42d/g — Snapshot bestaande overrides (per price_code_id) zodat ze
    // behouden blijven bij sjabloonwissel wanneer dezelfde price_code_id in
    // het nieuwe sjabloon voorkomt. Project-scope stickyness. Union met de
    // reeds op de zone bewaarde overrides (F42g durable snapshot).
    const overridesByPc = { ...(zone.saved_price_overrides || {}) };
    for (const meas of measurements) {
      if (meas.price_code_id && meas.unit_price_override != null && meas.unit_price_override !== "") {
        overridesByPc[meas.price_code_id] = +meas.unit_price_override;
      }
    }

    // Remove measurements from old template's lines (matched by price_code_id)
    const oldPcs = new Set((oldTpl?.template_lines || []).map((l) => l.price_code_id));
    // Only remove ones that WERE created by the previous template selection —
    // heuristic: measurements with a matching price_code_id AND formula that
    // matches the code's unit-derived formula (i.e. auto-created shape).
    for (const meas of measurements) {
      if (meas.price_code_id && oldPcs.has(meas.price_code_id)) {
        onRemoveMeasurement(meas.id);
      }
    }

    // Add default_checked lines from new template
    if (newTpl) {
      for (const ln of (newTpl.template_lines || [])) {
        if (ln.default_checked === false) continue;
        const m = buildMeasurementFromLine(ln);
        if (m) {
          // v6 F42d — Herstel bewaarde override voor dezelfde price_code.
          if (overridesByPc[ln.price_code_id] != null) {
            m.unit_price_override = overridesByPc[ln.price_code_id];
          }
          onAddMeasurement(m);
        }
      }
    }

    onUpdateZone({
      zone_template_id: tplId || null,
      // v6 F42k Correctieronde v4 — Wanneer de user actief het sjabloon wist
      // (tplId=""), markeer de zone zodat auto-select niet meteen opnieuw
      // dezelfde template terug plaatst.
      zone_template_cleared: !tplId,
      saved_price_overrides: overridesByPc,   // v6 F42g — persist durable snapshot
    });
  };

  // v6 F42k Correctieronde v4 — Single-template auto-select.
  // Wanneer er na context-filtering EXACT ÉÉN geldig actief sjabloon over
  // is (bv. Trapwerken → alleen "EMMA™ — Trapwerken"), selecteer dat
  // automatisch bij het aanmaken van een nieuwe zone zodat de werkposten
  // meteen zichtbaar zijn. Voorwaarden:
  //  • zone heeft nog GEEN zone_template_id (nooit gekozen);
  //  • zone is niet bewust gewist door user (`zone_template_cleared !== true`);
  //  • templates zijn geladen (voorkomt auto-select vóór data ready);
  //  • priceCodes zijn geladen (voorkomt lege measurements).
  // Bestaande zones met een keuze OF bewust gewiste zones blijven ongemoeid.
  const singleTemplateOnly = relevantTemplates.length === 1 ? relevantTemplates[0] : null;
  useEffect(() => {
    if (!singleTemplateOnly) return;
    if (activeTemplateId) return;
    if (zone.zone_template_cleared === true) return;
    if (!priceCodes.length) return;
    // v6 F42k Correctieronde v10 P0-FIX 1 — CRITICAL: bescherm bestaande
    // measurements tegen re-seeding bij remount. Zonder deze guard zou
    // een zone met reeds gekozen werkposten (bv. bij navigate-away+terug
    // naar de offerte) de auto-select opnieuw uitvoeren en alle user-
    // selecties + overrides overschrijven met de default_checked lines.
    if (measurements && measurements.length > 0) return;
    setTemplate(singleTemplateOnly.id);
  }, [singleTemplateOnly?.id, activeTemplateId, zone.zone_template_cleared, priceCodes.length, measurements?.length]);

  const toggleLine = (line) => {
    const existing = measByPc[line.price_code_id];
    // v6 F42g — Snapshot huidige overrides op de zone (durable), zodat ze
    // beschikbaar blijven na uitschakelen én na sjabloonwissel.
    const savedOverrides = { ...(zone.saved_price_overrides || {}) };

    if (existing) {
      // Uitschakelen: bewaar override (indien aanwezig) voor later herstel.
      if (existing.unit_price_override != null && existing.unit_price_override !== "") {
        savedOverrides[line.price_code_id] = +existing.unit_price_override;
      }
      onRemoveMeasurement(existing.id);
      onUpdateZone({ saved_price_overrides: savedOverrides });
      return;
    }

    // v6 F42g — Choice-group radio: verwijder eerst andere actieve regels in
    // dezelfde groep en bewaar hun overrides voor later herstel.
    const group = line.choice_group;
    if (group && activeTemplate?.template_lines?.length) {
      const groupPcIds = new Set(
        activeTemplate.template_lines
          .filter((L) => L.choice_group === group && L.price_code_id !== line.price_code_id)
          .map((L) => L.price_code_id)
      );
      for (const meas of measurements) {
        if (meas.price_code_id && groupPcIds.has(meas.price_code_id)) {
          if (meas.unit_price_override != null && meas.unit_price_override !== "") {
            savedOverrides[meas.price_code_id] = +meas.unit_price_override;
          }
          onRemoveMeasurement(meas.id);
        }
      }
    }

    // Aanzetten: build measurement + restore eerder bewaarde override.
    const m = buildMeasurementFromLine(line);
    if (m) {
      const savedForThis = savedOverrides[line.price_code_id];
      if (savedForThis != null) {
        m.unit_price_override = savedForThis;
      }
      onAddMeasurement(m);
    }
    onUpdateZone({ saved_price_overrides: savedOverrides });
  };

  // Zone subtotal for lines matching template
  const templatePcIds = new Set((activeTemplate?.template_lines || []).map((l) => l.price_code_id));
  const zoneSubtotal = (calc?.lines || [])
    .filter((ln) => ln.zone_id === zone.id)
    .reduce((s, ln) => s + (+ln.total_excl || 0), 0);

  // Extra manual works (not in the active template) — informational
  const extraManualCount = measurements.filter(
    (m) => m.price_code_id && !templatePcIds.has(m.price_code_id)
  ).length;

  return (
    <div
      className="border-2 rounded-xl p-3 space-y-3"
      style={{ borderColor: `${accentColor}55`, backgroundColor: `${accentColor}0A` }}
      data-testid={`zone-template-block-${zone.id}`}
    >
      {/* Sjabloon-picker
          v6 F42k Correctieronde v4 — Wanneer er exact 1 relevante template
          is EN die is actief geselecteerd (auto of manueel) EN er geen legacy/
          missing/wrong-ctx state is → toon een compacte "vaste sjabloon"-
          indicator i.p.v. de dropdown. User kan alsnog wissen via de ✕-knop
          zodat een handmatige keuze mogelijk blijft. */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259] mb-2">
          {label}
        </div>
        {(relevantTemplates.length === 1 && activeTemplate && !templateMissing && !templateWrongCtx && !templateLegacy) ? (
          <div
            className="h-10 w-full rounded-lg border border-[#E5E2DC] bg-white px-3 flex items-center justify-between gap-2 text-sm"
            data-testid={`zone-template-single-${zone.id}`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span
                className="text-[9px] uppercase tracking-[0.15em] font-bold text-white px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: accentColor }}
              >
                Auto
              </span>
              <span className="font-medium text-[#2C2C2A] truncate" data-testid={`zone-template-single-name-${zone.id}`}>
                {activeTemplate.name}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                const ok = window.confirm(
                  "Sjabloon wissen?\n\n" +
                  "De automatisch geplaatste werkposten verdwijnen. Extra werken die je zelf hebt toegevoegd blijven behouden. " +
                  "Je kan later opnieuw een sjabloon kiezen."
                );
                if (ok) setTemplate("");
              }}
              className="text-[10px] text-[#5D7259] hover:text-[#D9534F] underline decoration-dotted flex-shrink-0"
              data-testid={`zone-template-single-clear-${zone.id}`}
              title="Sjabloon wissen"
            >
              ✕ Wis
            </button>
          </div>
        ) : (
          <select
            value={(templateMissing || templateWrongCtx || templateLegacy) ? "" : activeTemplateId}
            onChange={(e) => {
              const newId = e.target.value;
              // v6 F16.2 — Bij migratie legacy → nieuw: bevestiging vragen
              if (templateLegacy && newId && newId !== activeTemplateId) {
                const ok = window.confirm(
                  "Oude sjabloonregels vervangen door nieuw sjabloon?\n\n" +
                  "De verouderde koppeling wordt vervangen. Extra werken die je zelf hebt toegevoegd blijven behouden."
                );
                if (!ok) return;
              }
              setTemplate(newId);
            }}
            className={`h-10 w-full rounded-lg border bg-white px-3 text-sm ${templateMissing ? "border-[#D9534F] text-[#D9534F]" : templateLegacy ? "border-[#E4B16D] text-[#7F4124] bg-[#FFF8ED]" : templateWrongCtx ? "border-[#E4B16D] text-[#7F4124]" : "border-[#E5E2DC]"}`}
            data-testid={`zone-template-select-${zone.id}`}
          >
            <option value="">
              {templateLegacy ? `⚠ Verouderd sjabloon — kies een nieuw actief sjabloon`
                : templateMissing ? "⚠ Sjabloon niet gevonden — kies een nieuwe"
                : templateWrongCtx ? "⚠ Vorige sjabloon past niet meer — kies een nieuwe"
                : "— Kies een sjabloon —"}
            </option>
            {relevantTemplates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
        {templateLegacy && (
          <div
            className="text-[11px] text-[#7F4124] mt-1.5 font-medium"
            data-testid={`zone-template-legacy-${zone.id}`}
          >
            Dit sjabloon is verouderd ({legacyTemplate?.name}). Kies een nieuw actief sjabloon uit de lijst hierboven. De oude sjabloonregels worden vervangen.
          </div>
        )}
        {templateMissing && (
          <div className="text-[11px] text-[#D9534F] mt-1.5 font-medium" data-testid={`zone-template-missing-${zone.id}`}>
            ⚠ De vorige sjabloon-koppeling ({activeTemplateId.slice(0, 8)}…) bestaat niet meer in de bibliotheek. Kies een nieuwe.
          </div>
        )}
        {!templateMissing && !templateWrongCtx && !templateLegacy && activeTemplate?.description && (
          <div className="text-[11px] text-[#5D7259] mt-1.5 italic">
            {activeTemplate.description}
          </div>
        )}
        {relevantTemplates.length === 0 && (
          <div className="text-[11px] text-[#4F4F4D] mt-1.5 italic">
            Geen relevante sjablonen beschikbaar. Gebruik "Code toevoegen" hieronder voor handmatige regels.
          </div>
        )}
      </div>

      {/* Header + zone subtotal */}
      <div className="flex items-center justify-between border-t border-[#E5E2DC] pt-3">
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
          Selecteer de werken
        </div>
        <div className="text-sm">
          <span className="text-[#4F4F4D]">Zone-totaal:</span>{" "}
          <strong className="text-[#2C2C2A]" data-testid={`zone-template-subtotal-${zone.id}`}>
            {fmtEUR(zoneSubtotal)}
          </strong>
        </div>
      </div>

      {!activeTemplate && !templateMissing && !templateWrongCtx && (
        <div className="text-sm text-[#4F4F4D] italic py-3 text-center border border-dashed border-[#E5E2DC] rounded-lg">
          Kies eerst een sjabloon om de werken te zien.
        </div>
      )}

      {/* Template lines → toggleable cards + inline editor */}
      {activeTemplate && (
        <div className="border border-[#E5E2DC] rounded-xl overflow-hidden divide-y divide-[#F0EFEA] bg-white">
          {(activeTemplate.template_lines || []).map((line) => (
            <WorkLineCard
              key={line.id || line.price_code_id}
              line={line}
              pc={pcById[line.price_code_id]}
              measurement={measByPc[line.price_code_id]}
              zoneNet={+zoneQty || 0}
              stairTreads={stairTreads}
              mode={mode}
              zoneId={zone.id}
              accentColor={accentColor}
              calcLines={calc?.lines || []}
              onToggle={() => toggleLine(line)}
              onUpdateMeasurement={onUpdateMeasurement}
            />
          ))}
        </div>
      )}

      {extraManualCount > 0 && (
        <div className="text-[11px] text-[#7F4124] italic pt-1">
          ℹ️ {extraManualCount} handmatige regel(s) buiten dit sjabloon — zichtbaar in de meetlijst hieronder.
        </div>
      )}
    </div>
  );
}

// ---------- one toggleable work-line card (with inline editor) ----------
function WorkLineCard({
  line, pc, measurement, zoneNet, stairTreads, mode, zoneId, accentColor,
  calcLines, onToggle, onUpdateMeasurement,
}) {
  const checked = !!measurement;
  // v6 F42k v14b (final polish) — Description precedence:
  //   measurement.description_override → measurement.description_snapshot →
  //   line.description_override (template-niveau) → pc.description → "—"
  const measDescOverride = checked ? (measurement.description_override || null) : null;
  const measDescSnapshot = checked ? (measurement.description_snapshot || null) : null;
  const templateDescDefault = line.description_override || pc?.description || "—";
  const effDescDefault = measDescSnapshot || templateDescDefault;
  const description = measDescOverride || effDescDefault;
  const code = pc?.code || "—";
  const unit = pc?.unit || "m2";
  const unitLabel = UNIT_LABEL[unit] || unit;
  // v6 F42d — Effectieve eenheidsprijs cascade:
  //   measurement.unit_price_override → template line default → price code default
  const defaultPrice = pc ? +pc.default_price : 0;
  const templateDefault = line.unit_price_override != null ? +line.unit_price_override : null;
  const measurementOverride = (checked && measurement && measurement.unit_price_override != null && measurement.unit_price_override !== "")
    ? +measurement.unit_price_override
    : null;
  const unitPrice = measurementOverride ?? templateDefault ?? defaultPrice;

  // Live line total + qty from calc (server-side truth) when checked.
  const calcLine = checked ? calcLines.find((l) => l.item_id === measurement?.id) : null;
  const displayQty = calcLine?.quantity ?? 0;
  const lineTotal = calcLine?.total_excl ?? 0;

  // Is m² currently using zone_net formula? (stair opt-in indicator)
  const usesZoneArea = checked && measurement?.formula === "zone_net";
  const toggleUseZoneArea = () => {
    if (!checked || !onUpdateMeasurement) return;
    if (usesZoneArea) {
      onUpdateMeasurement(measurement.id, { formula: "custom", quantity: 0 });
    } else {
      onUpdateMeasurement(measurement.id, { formula: "zone_net", quantity: 0 });
    }
  };

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 ${checked ? "bg-[#FFF8F2]" : "hover:bg-[#FAF9F6]"}`}
      data-testid={`zone-work-row-${zoneId}-${line.price_code_id}`}
    >
      {/* Toggle — zelfde vorm/positie als joinery */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition ${
          checked ? "text-white" : "border-[#C9C8C2] hover:border-[#B07A4F]"
        }`}
        style={checked ? { backgroundColor: accentColor, borderColor: accentColor } : {}}
        data-testid={`zone-work-toggle-${zoneId}-${line.price_code_id}`}
        aria-label={checked ? "Werk verwijderen" : "Werk toevoegen"}
      >
        {checked && <Check className="w-4 h-4" />}
      </button>

      {/* Description + code · category (+ auto-hint + mobile summary + manual-qty editors) */}
      <div className="flex-1 min-w-0">
        {checked && onUpdateMeasurement ? (
          <div className="text-sm font-medium text-[#2C2C2A]">
            <EditableDescription
              value={description}
              override={measDescOverride}
              defaultValue={effDescDefault}
              onOverride={(text) => onUpdateMeasurement(measurement.id, { description_override: text })}
              onReset={() => onUpdateMeasurement(measurement.id, { description_override: null })}
              testId={`zone-work-desc-${zoneId}-${line.price_code_id}`}
            />
          </div>
        ) : (
          <div className="text-sm font-medium text-[#2C2C2A] line-clamp-2 sm:truncate">{description}</div>
        )}
        <div className="text-[10px] text-[#5D7259] font-mono truncate">
          {code} · {pc?.work_category || "—"}
        </div>
        {/* v6 F42e — Helpertekst Auto behouden, alleen wanneer m² + zone_net actief. */}
        {checked && unit === "m2" && mode !== "stair" && measurement?.formula === "zone_net" && (
          <div
            className="text-[10px] text-[#4F4F4D] italic mt-0.5"
            data-testid={`zone-work-auto-hint-${zoneId}-${line.price_code_id}`}
          >
            Auto: {displayQty.toFixed(2)} m² (netto geveloppervlakte)
          </div>
        )}
        {/* Mobiele prijs-samenvatting per regel (sm:hidden) — identiek patroon aan joinery */}
        <div
          className="sm:hidden text-[11px] font-mono text-[#2C2C2A] mt-1"
          data-testid={`zone-work-summary-mobile-${zoneId}-${line.price_code_id}`}
        >
          {unit === "fixed"
            ? `1 forfait × ${fmtEUR(unitPrice)}`
            : `${(+displayQty || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unitLabel} × ${fmtEUR(unitPrice)}/${unitLabel}`}
        </div>
        {/* v6 F42e — Manuele qty-editors voor lm/piece/hour/stair-m²: blijven
            inline onder de omschrijving (joinery heeft dit niet nodig omdat
            qty daar altijd via auto_source komt; gevel heeft handmatige units).
            Prijsinput staat NIET meer hier — die staat in de vaste €-kolom. */}
        {checked && onUpdateMeasurement && (unit === "lm" || (unit === "piece" && mode !== "stair") || unit === "hour" || (unit === "m2" && mode === "stair")) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
            {unit === "lm" && (
              <div className="flex items-center gap-2">
                <span className="text-[#5D7259] font-medium">Lengte:</span>
                <RawNumInput
                  value={+measurement.length || 0}
                  onChange={(v) => onUpdateMeasurement(measurement.id, { length: +v || 0 })}
                  className="h-8 w-24 rounded-md border border-[#E5E2DC] px-2 text-right font-mono text-sm"
                  data-testid={`zone-work-length-${zoneId}-${line.price_code_id}`}
                />
                <span className="text-[#5D7259]">lm</span>
              </div>
            )}
            {unit === "m2" && mode === "stair" && (
              <label className="flex items-center gap-2 cursor-pointer text-[#5D7259]">
                <input
                  type="checkbox"
                  checked={usesZoneArea}
                  onChange={toggleUseZoneArea}
                  className="w-4 h-4 accent-[#7F4124]"
                  data-testid={`zone-work-use-area-${zoneId}-${line.price_code_id}`}
                />
                <span>Gebruik trapoppervlakte ({zoneNet.toFixed(2)} m²)</span>
              </label>
            )}
            {unit === "m2" && mode === "stair" && !usesZoneArea && (
              <div className="flex items-center gap-2">
                <span className="text-[#5D7259] font-medium">Manueel:</span>
                <RawNumInput
                  value={+measurement.quantity || 0}
                  onChange={(v) => onUpdateMeasurement(measurement.id, { quantity: +v || 0 })}
                  className="h-8 w-24 rounded-md border border-[#E5E2DC] px-2 text-right font-mono text-sm"
                  data-testid={`zone-work-manual-qty-${zoneId}-${line.price_code_id}`}
                />
                <span className="text-[#5D7259]">m²</span>
              </div>
            )}
            {unit === "piece" && mode !== "stair" && (
              <div className="flex items-center gap-2">
                <span className="text-[#5D7259] font-medium">Aantal:</span>
                <RawNumInput
                  value={+measurement.quantity || 0}
                  onChange={(v) => onUpdateMeasurement(measurement.id, { quantity: +v || 0 })}
                  className="h-8 w-24 rounded-md border border-[#E5E2DC] px-2 text-right font-mono text-sm"
                  data-testid={`zone-work-piece-qty-${zoneId}-${line.price_code_id}`}
                />
                <span className="text-[#5D7259]">stuk</span>
              </div>
            )}
            {unit === "hour" && (
              <div className="flex items-center gap-2">
                <span className="text-[#5D7259] font-medium">Uren:</span>
                <RawNumInput
                  value={+measurement.quantity || 0}
                  onChange={(v) => onUpdateMeasurement(measurement.id, { quantity: +v || 0 })}
                  className="h-8 w-24 rounded-md border border-[#E5E2DC] px-2 text-right font-mono text-sm"
                  data-testid={`zone-work-hours-${zoneId}-${line.price_code_id}`}
                />
                <span className="text-[#5D7259]">u</span>
              </div>
            )}
          </div>
        )}
        {/* v6 F42e — Piece op stair-mode: qty volgt aantal treden (read-only hint) */}
        {checked && unit === "piece" && mode === "stair" && (
          <div className="mt-1 text-[10px] text-[#4F4F4D] italic" data-testid={`zone-work-piece-stair-hint-${zoneId}-${line.price_code_id}`}>
            Auto: {stairTreads} treden (volgt aantal treden)
          </div>
        )}
      </div>

      {/* Unit — sm+ (zelfde kolom als joinery) */}
      <div className="text-[11px] text-[#4F4F4D] hidden sm:block w-12 text-right">{unitLabel}</div>

      {/* v6 F42e — Prijs-kolom uniform met joinery. Input verschijnt wanneer
          checked, statisch label wanneer niet aangevinkt. Placeholder toont de
          default price (template of pc). Empty → override=null (fallback). */}
      <div className="hidden sm:flex flex-wrap items-center gap-x-1 gap-y-0.5 w-24 justify-end">
        {checked && onUpdateMeasurement ? (
          <>
            <span className="text-[11px] text-[#4F4F4D]">€</span>
            <RawNumInput
              value={measurementOverride != null ? measurementOverride : ""}
              allowEmpty
              onChange={(v) => {
                if (v === "" || v === null || v === undefined) {
                  onUpdateMeasurement(measurement.id, { unit_price_override: null });
                } else {
                  onUpdateMeasurement(measurement.id, { unit_price_override: +v });
                }
              }}
              placeholder={(templateDefault ?? defaultPrice).toFixed(2)}
              className="w-16 h-8 rounded-md text-right text-sm"
              data-testid={`zone-work-price-${zoneId}-${line.price_code_id}`}
            />
            {measurementOverride != null && (
              <span
                className="text-[9px] uppercase tracking-wider font-bold text-[#B07A4F]"
                data-testid={`zone-work-price-override-badge-${zoneId}-${line.price_code_id}`}
                title="Aangepast voor dit project — centrale prijslijst blijft ongewijzigd"
              >
                Aangepast
              </span>
            )}
          </>
        ) : (
          <span className="text-[11px] text-[#4F4F4D]">€ {unitPrice.toFixed(2)}/{unitLabel}</span>
        )}
      </div>

      {/* Qty — zelfde kolombreedte als joinery */}
      <div className="w-20 text-right">
        <div
          className="text-sm font-bold text-[#2C2C2A]"
          data-testid={`zone-work-qty-${zoneId}-${line.price_code_id}`}
        >
          {(+displayQty || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
        </div>
        {/* Mobile total onder qty (sm:hidden), identiek aan joinery */}
        <div
          className={`sm:hidden text-sm font-bold mt-0.5 ${checked ? "" : "text-[#A8A7A0]"}`}
          style={checked ? { color: accentColor } : {}}
          data-testid={`zone-work-total-mobile-${zoneId}-${line.price_code_id}`}
        >
          {fmtEUR(checked ? lineTotal : 0)}
        </div>
      </div>

      {/* Total — sm+ (zelfde kolombreedte als joinery) */}
      <div className="w-24 text-right hidden sm:block">
        <div
          className={`text-sm font-bold ${checked ? "" : "text-[#A8A7A0]"}`}
          style={checked ? { color: accentColor } : {}}
          data-testid={`zone-work-total-${zoneId}-${line.price_code_id}`}
        >
          {fmtEUR(checked ? lineTotal : 0)}
        </div>
      </div>
    </div>
  );
}
